import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  MutationError,
  latestError,
  ProblemError,
  downloadBlob,
  hasGrant,
  useApi,
  usePrincipal,
} from "@csa/web-foundation";
import { useT } from "../../i18n/index";
import { Icon } from "../../ui/icons";
import { useCreateApplication } from "../issuance/use-issuance";
import { SuitabilityRanking } from "./suitability-ranking";
import {
  useChooseOffer,
  useRecommendation,
  useRecordDecision,
  useScoreOffers,
  useTier2Review,
} from "./use-recommendation";
import {
  useApproveApproval,
  useAwaitingMe,
  useMyApprovals,
  useRejectApproval,
  useRequestApproval,
} from "./use-approvals";
import {
  RECOMMENDATION_TIER2,
  type CustomerDecision,
  type CustomerProposalResponse,
  type Recommendation,
  type RecommendationDetail as RecDetail,
  type RecommendationOffer,
} from "./types";
import { RESPONSE_LABEL_KEY, responseDate } from "./response-labels";

export function RecommendationDetail() {
  const { id = "" } = useParams();
  const { t } = useT();
  const { data, isLoading, error } = useRecommendation(id);

  if (isLoading)
    return (
      <div className="k-loading">
        <div className="k-loading__head">
          <span className="k-loading__spinner" />
          <span className="k-loading__label">{t("rec.loading")}</span>
        </div>
      </div>
    );
  if (error || !data)
    return (
      <div className="k-note k-note--error">
        <div className="k-note__row">
          <span className="k-note__icon k-note__icon--error">
            <Icon name="alert-triangle" size={20} />
          </span>
          <p role="alert" className="k-note__desc">
            {error instanceof ProblemError ? error.problem.detail : t("rec.error")}
          </p>
        </div>
      </div>
    );

  const { recommendation: rec, offers, customerResponses } = data;
  const statusVariant =
    rec.status === "PRESENTED" || rec.status === "DECIDED"
      ? "active"
      : rec.status === "PENDING_TIER2"
        ? "review"
        : rec.status === "ABANDONED"
          ? "ended"
          : "pending";
  return (
    <section className="surface">
      <header className="surface__head">
        <div>
          <h1 className="surface__title">{t("rec.heading")}</h1>
          <p className="surface__subtitle">
            {t("rec.status")}: <strong data-testid="rec-status">{rec.status}</strong> ·{" "}
            {rec.lineCode}
          </p>
        </div>
        <span className={`k-status k-status--${statusVariant}`}>
          <span className="k-status__dot" />
          {rec.status}
        </span>
      </header>

      {rec.status === "DRAFT" && offers.length === 0 && <OfferEntry id={id} />}
      {rec.status === "DRAFT" && offers.length > 0 && <ChooseStage id={id} offers={offers} />}
      {rec.status === "PENDING_TIER2" && (
        <>
          <Tier2Panel id={id} />
          <SuitabilityRanking offers={offers} />
        </>
      )}
      {rec.status === "PRESENTED" && (
        <>
          {/* The customer's response, right where the advisor binds — see + bind on one screen. */}
          <CustomerResponsePanel responses={customerResponses} />
          <DecisionPanel id={id} />
          <ProposalDelivery rec={rec} />
          <SuitabilityRanking offers={offers} />
        </>
      )}
      {rec.status === "DECIDED" && (
        <>
          <CustomerResponsePanel responses={customerResponses} />
          <p data-testid="decided" className="k-status k-status--active">
            <Icon name="check-circle" size={16} />
            {t("rec.decided")}: <strong>{rec.customerDecision}</strong>
          </p>
          {/* F2-F — the advisor turns an ACCEPTED decision into a policy application (no
              auto-bind; the advisor triggers). Only for ACCEPTED — never RET/DEFERRED. */}
          {rec.customerDecision === "ACCEPTED" && <IssueStage rec={rec} offers={offers} />}
          <ProposalDelivery rec={rec} />
          <SuitabilityRanking offers={offers} />
        </>
      )}
    </section>
  );
}

// F2-F — "Poliçeleştir". Creates a NEW_BUSINESS application from the recommendation's chosen
// offer, then hands off to the issuance desk (/issuance/:id) where submit → observe-payment
// → confirm-issuance continues. RBAC is UX (application:write; the API enforces). The button
// is disabled when the insured asset is missing (the create schema requires it) or the chosen
// offer can't be resolved. acceptedAt/quoteValidUntil are DERIVED server-side (F2-B).
function IssueStage({ rec, offers }: { rec: Recommendation; offers: RecommendationOffer[] }) {
  const { t } = useT();
  const navigate = useNavigate();
  const { me } = usePrincipal();
  const create = useCreateApplication();

  if (!hasGrant(me, "application:write", "all")) return null; // no affordance without the grant

  const chosen = offers.find((o) => o.id === rec.chosenOfferId);
  const missingAsset = rec.insuredAssetId === null;

  function issue() {
    if (!chosen || !rec.insuredAssetId) return;
    create.mutate(
      {
        applicationType: "NEW_BUSINESS",
        householdId: rec.householdId,
        insuredAssetId: rec.insuredAssetId,
        lineCode: rec.lineCode,
        carrierId: chosen.carrierId,
        acceptedOfferId: chosen.offerId, // the quote_offer soft-ref = the accepted offer
        recommendationId: rec.id,
      },
      { onSuccess: (app) => navigate(`/issuance/${app.id}`) },
    );
  }

  return (
    <div className="rec-issue rec-stage k-card" data-testid="issue-stage">
      <button
        type="button"
        className="k-btn k-btn--primary"
        data-testid="issue-btn"
        disabled={!chosen || missingAsset || create.isPending}
        onClick={issue}
      >
        <Icon name="document" size={16} />
        {t("rec.issue")}
      </button>
      {missingAsset && (
        <p className="muted small" data-testid="issue-no-asset" style={{ marginTop: 10 }}>
          {t("rec.issueNoAsset")}
        </p>
      )}
      {create.error instanceof ProblemError && (
        <p role="alert" className="k-field__error" data-testid="issue-error">
          {create.error.problem.detail}
        </p>
      )}
    </div>
  );
}

// PROP-PDF-F — the advisor's delivery affordances on a PRESENTED/DECIDED proposal: download
// the branded proposal-form PDF, and mint a short-lived signed link to paste into WhatsApp.
// The mint (POST proposal-link) returns { token, path, expiresInSeconds } where `path` is the
// relative /p/:token; the full URL is built here by prefixing the app origin, then copied to
// the clipboard — and shown so the advisor can copy it by hand if the clipboard is blocked.
// RBAC is UX: viewing the rec already implies recommendation:read; the API enforces both calls.
function ProposalDelivery({ rec }: { rec: Recommendation }) {
  const { t } = useT();
  const api = useApi();
  const [pdf, setPdf] = useState<"idle" | "loading" | "error">("idle");
  const [linkState, setLinkState] = useState<"idle" | "loading" | "error">("idle");
  const [link, setLink] = useState<{ url: string; copied: boolean } | null>(null);

  async function downloadPdf() {
    setPdf("loading");
    try {
      const blob = await api.getBlob(`/v1/recommendations/${rec.id}/proposal.pdf`, {
        accept: "application/pdf",
      });
      downloadBlob(blob, "teklif.pdf");
      setPdf("idle");
    } catch {
      setPdf("error");
    }
  }

  async function createLink() {
    setLinkState("loading");
    try {
      const res = await api.post<{ token: string; path: string; expiresInSeconds: number }>(
        `/v1/recommendations/${rec.id}/proposal-link`,
      );
      const origin = globalThis.location?.origin ?? "";
      const url = `${origin}${res.path}`;
      let copied = false;
      try {
        await globalThis.navigator?.clipboard?.writeText(url);
        copied = true;
      } catch {
        copied = false; // clipboard blocked (permission/insecure ctx) → show for manual copy
      }
      setLink({ url, copied });
      setLinkState("idle");
    } catch {
      setLinkState("error");
    }
  }

  return (
    <div className="rec-stage rec-delivery k-card" data-testid="proposal-delivery">
      <p className="surface__section-label" style={{ marginTop: 0 }}>
        {t("rec.delivery.title")}
      </p>
      <div className="rec-delivery-actions">
        <button
          type="button"
          className="k-btn k-btn--secondary"
          data-testid="pdf-download"
          disabled={pdf === "loading"}
          onClick={downloadPdf}
        >
          <Icon name="file" size={16} />
          {pdf === "loading" ? t("rec.delivery.pdfLoading") : t("rec.delivery.pdf")}
        </button>
        <button
          type="button"
          className="k-btn k-btn--primary"
          data-testid="link-create"
          disabled={linkState === "loading"}
          onClick={createLink}
        >
          <Icon name="conversation" size={16} />
          {linkState === "loading" ? t("rec.delivery.linkLoading") : t("rec.delivery.link")}
        </button>
      </div>
      {pdf === "error" && (
        <p role="alert" className="k-field__error" data-testid="pdf-error">
          {t("rec.delivery.pdfError")}
        </p>
      )}
      {linkState === "error" && (
        <p role="alert" className="k-field__error" data-testid="link-error">
          {t("rec.delivery.linkError")}
        </p>
      )}
      {link && (
        <div className="rec-delivery-link" data-testid="link-result">
          <p className="muted small">
            {link.copied ? t("rec.delivery.copied") : t("rec.delivery.copyManual")}
          </p>
          <code data-testid="link-url">{link.url}</code>
          <p className="muted small">{t("rec.delivery.expiry")}</p>
        </div>
      )}
    </div>
  );
}

// The customer's response (I2a fold-in), display-only — append-only from both sides,
// no edit/delete affordance. The latest (index 0, newest-first) is prominent; earlier
// responses show beneath so a DEFERRED→ACCEPTED change of mind is visible.
function CustomerResponsePanel({ responses }: { responses: CustomerProposalResponse[] }) {
  const { t } = useT();
  const [latest, ...earlier] = responses;
  if (!latest) return null;
  return (
    <div className="rec-stage rec-customer-response k-card" data-testid="customer-response">
      <p className="surface__section-label" style={{ marginTop: 0 }}>
        {t("rec.customerResponse.heading")}
      </p>
      <p className="k-status k-status--review" style={{ margin: 0 }}>
        <Icon name="conversation" size={16} />
        {t("rec.customerResponse.said")} <strong>{t(RESPONSE_LABEL_KEY[latest.response])}</strong> ·{" "}
        {responseDate(latest.respondedAt)}
      </p>
      {earlier.length > 0 && (
        <>
          <p className="muted small" style={{ marginBottom: 4 }}>
            {t("rec.customerResponse.history")}
          </p>
          <ul className="muted" data-testid="customer-response-history">
            {earlier.map((r) => (
              <li key={r.id}>
                {t(RESPONSE_LABEL_KEY[r.response])} · {responseDate(r.respondedAt)}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function OfferEntry({ id }: { id: string }) {
  const { t } = useT();
  const [ids, setIds] = useState("");
  const score = useScoreOffers(id);
  const offerIds = ids
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return (
    <div className="rec-stage k-card">
      <p className="surface__section-label" style={{ marginTop: 0 }}>
        {t("rec.offerEntry")}
      </p>
      {/* GAP-AUDIT-3 D20 — reclassified from "dead control" to a rough-but-honest one.
          A picker over GET /v1/quotes/:id/offers is NOT buildable today: the
          recommendation carries no quote reference, and GET /v1/quotes takes only
          cursor/limit — no partyId/lineCode filter — so the only way to find the
          right quote from here would be to page the caller's entire quote list and
          filter in the browser. That is the same unbounded-scan pattern D13 was
          about, so it is a backlog NEW-API (GET /v1/quotes?partyId=&lineCode=), not
          something to fake. What WAS wrong is that the field said "Teklif (offer)
          kimliği" and left the advisor to guess where that comes from; the hint now
          says it. */}
      <label className="k-field">
        <span className="k-field__label">{t("rec.offerId")}</span>
        <textarea
          className="k-input"
          aria-label={t("rec.offerId")}
          value={ids}
          onChange={(e) => setIds(e.target.value)}
          rows={3}
        />
        <span className="k-field__hint" data-testid="rec-offer-id-hint">
          {t("rec.offerIdHint")}
        </span>
      </label>
      <button
        type="button"
        className="k-btn k-btn--primary"
        disabled={offerIds.length === 0 || score.isPending}
        onClick={() => score.mutate(offerIds)}
      >
        <Icon name="scorecard" size={16} />
        {t("rec.score")}
      </button>
      {/* A bad offer id 400s; the screen said nothing and the offers stayed
          unscored, which reads as "the button does nothing". */}
      <MutationError
        error={score.error}
        fallback={t("rec.actionFailed")}
        offline={t("common.offline")}
      />
    </div>
  );
}

function ChooseStage({ id, offers }: { id: string; offers: RecommendationOffer[] }) {
  const { t } = useT();
  const choose = useChooseOffer(id);
  const [selected, setSelected] = useState<RecommendationOffer | null>(null);
  const [reasonCode, setReasonCode] = useState("");
  const [rationale, setRationale] = useState("");
  // OPTIONAL advisor→customer note — never gates submit (SEPARATE from the mandatory
  // cheaper-but rationale). Sent only when non-empty.
  const [advisorNote, setAdvisorNote] = useState("");
  const ADVISOR_NOTE_MAX = 300;

  const cheapest = Math.min(...offers.map((o) => Number(o.grossPremium)));
  const nonCheapest = selected !== null && Number(selected.grossPremium) > cheapest;
  const blocked = nonCheapest && rationale.trim().length === 0;

  return (
    <div className="rec-stage">
      <SuitabilityRanking offers={offers} onChoose={setSelected} />
      {selected && (
        <form
          className="rec-choose-form k-card"
          onSubmit={(e) => {
            e.preventDefault();
            if (blocked) return;
            const note = advisorNote.trim();
            choose.mutate({
              chosenOfferId: selected.id,
              reasonCode: reasonCode.trim() || "ADVISOR_SELECTION",
              cheaperButRationale: nonCheapest ? rationale.trim() : undefined,
              // Optional: omit entirely when blank (match the schema's optional semantics).
              advisorNote: note.length > 0 ? note : undefined,
            });
          }}
        >
          <p style={{ margin: "0 0 4px" }}>
            {t("rec.choose")}: <strong>{selected.carrierId}</strong> ({selected.grossPremium})
          </p>
          <label className="k-field">
            <span className="k-field__label">{t("rec.reasonCode")}</span>
            <input
              className="k-input"
              aria-label={t("rec.reasonCode")}
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
            />
          </label>
          {nonCheapest && (
            <label className="k-field">
              <span className="k-field__label">{t("rec.rationale")}</span>
              <input
                className="k-input"
                aria-label={t("rec.rationale")}
                value={rationale}
                onChange={(e) => setRationale(e.target.value)}
              />
            </label>
          )}
          {blocked && (
            <p role="alert" className="k-field__error">
              {t("rec.rationaleRequired")}
            </p>
          )}
          <label className="k-field">
            <span className="k-field__label">{t("rec.advisorNote")}</span>
            <textarea
              className="k-input"
              aria-label={t("rec.advisorNote")}
              data-testid="advisor-note"
              value={advisorNote}
              onChange={(e) => setAdvisorNote(e.target.value.slice(0, ADVISOR_NOTE_MAX))}
              maxLength={ADVISOR_NOTE_MAX}
              rows={2}
            />
            <span className="k-field__hint" data-testid="advisor-note-hint">
              {t("rec.advisorNoteHint")}
            </span>
            <span
              className="muted small"
              data-testid="advisor-note-counter"
              aria-label={t("rec.advisorNoteCounter")}
            >
              {advisorNote.length}/{ADVISOR_NOTE_MAX}
            </span>
          </label>
          <button
            type="submit"
            className="k-btn k-btn--primary"
            disabled={blocked || choose.isPending}
          >
            <Icon name="check" size={16} />
            {t("rec.confirmChoice")}
          </button>
          {/* `blocked` covers only the client-side non-cheapest rule; the SERVER
              re-applies it and can also refuse for reasons the client cannot see
              (state, scope, staleness). Those were silent. */}
          <MutationError
            error={choose.error}
            fallback={t("rec.actionFailed")}
            offline={t("common.offline")}
          />
        </form>
      )}
    </div>
  );
}

function Tier2Panel({ id }: { id: string }) {
  const { t } = useT();
  const { me } = usePrincipal();
  const mine = useMyApprovals();
  const awaiting = useAwaitingMe();
  const request = useRequestApproval();
  const approve = useApproveApproval();
  const reject = useRejectApproval();
  const tier2 = useTier2Review(id);
  // Decline reason was collected via globalThis.prompt?.() (dead where prompt is
  // unavailable). Inline, opened from the Decline button.
  const [declining, setDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState("");

  const forThis = (a: { resourceId: string | null; action: string }) =>
    a.resourceId === id && a.action === RECOMMENDATION_TIER2;
  const myApproval = (mine.data ?? []).find(forThis);
  const toApprove = (awaiting.data ?? []).find(forThis);
  const canRequest = hasGrant(me, "recommendation:write", "own");

  return (
    <div className="rec-stage rec-tier2 k-card k-card--accent-amber" data-testid="tier2-panel">
      <p className="k-status k-status--review" style={{ marginTop: 0 }}>
        <Icon name="lock" size={16} />
        {t("rec.pendingTier2")}
      </p>

      {/* Requester (advisor) lane */}
      {myApproval?.status === "APPROVED" && (
        <button
          type="button"
          className="k-btn k-btn--approve"
          disabled={tier2.isPending}
          onClick={() => tier2.mutate(myApproval.id)}
        >
          <Icon name="check-circle" size={16} />
          {t("rec.tier2.complete")}
        </button>
      )}
      {myApproval?.status === "PENDING" && <p className="muted">{t("rec.tier2.awaiting")}</p>}
      {!myApproval && !toApprove && canRequest && (
        <button
          type="button"
          className="k-btn k-btn--primary"
          disabled={request.isPending}
          onClick={() => request.mutate({ action: RECOMMENDATION_TIER2, resourceId: id })}
        >
          <Icon name="tier" size={16} />
          {t("rec.tier2.requestApproval")}
        </button>
      )}

      {/* Approver (second person) lane — the awaiting-me entry for this rec */}
      {toApprove && (
        <div className="rec-approver" data-testid="approver-lane">
          <button
            type="button"
            className="k-btn k-btn--approve"
            disabled={approve.isPending}
            onClick={() => approve.mutate(toApprove.id)}
          >
            <Icon name="check" size={16} />
            {t("rec.tier2.grant")}
          </button>
          <button
            type="button"
            className="k-btn k-btn--danger"
            disabled={reject.isPending}
            onClick={() => setDeclining((v) => !v)}
          >
            {t("rec.tier2.decline")}
          </button>
          {declining && (
            <div className="rec-decline-form" data-testid="tier2-decline-form">
              <input
                className="k-input"
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder={t("rec.tier2.declineReason")}
                data-testid="tier2-decline-reason"
              />
              <button
                type="button"
                className="k-btn k-btn--danger k-btn--sm"
                disabled={reject.isPending || !declineReason.trim()}
                onClick={() =>
                  reject.mutate({ approvalId: toApprove.id, reason: declineReason.trim() })
                }
                data-testid="tier2-decline-confirm"
              >
                {t("rec.tier2.decline")}
              </button>
            </div>
          )}
        </div>
      )}
      {!myApproval && !toApprove && !canRequest && (
        <p className="muted">{t("rec.tier2.noApproval")}</p>
      )}
      {/* The four-eyes lane: request, grant and decline all fired and none
          reported. Same class as the approver inbox, same consequence — someone
          believes they granted an approval they did not. */}
      <MutationError
        error={latestError(request, approve, reject)}
        fallback={t("rec.actionFailed")}
        offline={t("common.offline")}
      />
    </div>
  );
}

function DecisionPanel({ id }: { id: string }) {
  const { t } = useT();
  const decide = useRecordDecision(id);
  const [reason, setReason] = useState("");
  const record = (customerDecision: CustomerDecision) =>
    decide.mutate({
      customerDecision,
      declineReason: customerDecision === "DECLINED" ? reason.trim() || "n/a" : undefined,
    });
  // DEC-OVR-F — the decision affordance is shown unconditionally (RBAC is UX): an all-scope
  // supervisor sees it on another advisor's PRESENTED rec, and the API enforces the write.
  // A rejected decision (missing grant / validation / four-eyes) surfaces the server's
  // message here instead of failing silently.
  return (
    <div className="rec-stage k-card" data-testid="decision-panel">
      <p className="surface__section-label" style={{ marginTop: 0 }}>
        {t("rec.present")}
      </p>
      <label className="k-field">
        <span className="k-field__label">{t("rec.declineReason")}</span>
        <input
          className="k-input"
          aria-label={t("rec.declineReason")}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      <div className="rec-decision-actions">
        <button
          type="button"
          className="k-btn k-btn--approve"
          disabled={decide.isPending}
          onClick={() => record("ACCEPTED")}
        >
          <Icon name="check" size={16} />
          {t("rec.accepted")}
        </button>
        <button
          type="button"
          className="k-btn k-btn--secondary"
          disabled={decide.isPending}
          onClick={() => record("DECLINED")}
        >
          {t("rec.declined")}
        </button>
        <button
          type="button"
          className="k-btn k-btn--soft"
          disabled={decide.isPending}
          onClick={() => record("DEFERRED")}
        >
          {t("rec.deferred")}
        </button>
      </div>
      {decide.error instanceof ProblemError && (
        <p role="alert" className="k-field__error" data-testid="decision-error">
          {decide.error.problem.detail}
        </p>
      )}
    </div>
  );
}

export type { RecDetail };
